
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradeHistoryByIdsParams struct
 */
class TradeHistoryByIdsParams {
  /**
   * Initialize struct
   * @param {!Array=} recordIds
   * @param {!boolean=} readFromArchive
   * @param {!boolean=} useSnapshot
   * @constructor
   */
  constructor (recordIds = [], readFromArchive = false, useSnapshot = false) {
    this.RecordIds = recordIds
    this.ReadFromArchive = readFromArchive
    this.UseSnapshot = useSnapshot
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradeHistoryByIdsParams}
   * @param {!TradeHistoryByIdsParams} other Other struct
   * @returns {!TradeHistoryByIdsParams} This struct
   */
  copy (other) {
    if (other.RecordIds != null) {
      this.RecordIds = []
      for (let item of other.RecordIds) {
        if (item != null) {
          let tempItem
          tempItem = UInt64.fromNumber(item)
          this.RecordIds.push(tempItem)
        } else {
          this.RecordIds.push(null)
        }
      }
    } else {
      this.RecordIds = null
    }
    if (other.ReadFromArchive != null) {
      this.ReadFromArchive = other.ReadFromArchive
    } else {
      this.ReadFromArchive = null
    }
    if (other.UseSnapshot != null) {
      this.UseSnapshot = other.UseSnapshot
    } else {
      this.UseSnapshot = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradeHistoryByIdsParams}
   * @returns {!TradeHistoryByIdsParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradeHistoryByIdsParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradeHistoryByIdsParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradeHistoryByIdsParams}
   * @param {!TradeHistoryByIdsParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradeHistoryByIdsParams)) {
      throw new TypeError('Instance of TradeHistoryByIdsParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradeHistoryByIdsParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      RecordIds: ((this.RecordIds != null) ? Array.from(this.RecordIds, item => ((item != null) ? item.toNumber() : null)) : null),
      ReadFromArchive: ((this.ReadFromArchive != null) ? this.ReadFromArchive : null),
      UseSnapshot: ((this.UseSnapshot != null) ? this.UseSnapshot : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradeHistoryByIdsParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradeHistoryByIdsParams} other Object value
   * @returns {!TradeHistoryByIdsParams} Created struct
   */
  static fromObject (other) {
    return new TradeHistoryByIdsParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradeHistoryByIdsParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradeHistoryByIdsParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradeHistoryByIdsParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4219
  }
}

export { TradeHistoryByIdsParams };

/**
 * Fast Binary Encoding TradeHistoryByIdsParams field model
 */
class FieldModelTradeHistoryByIdsParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.RecordIds = new fbe.FieldModelVector(new fbe.FieldModelUInt64(buffer, 4 + 4), buffer, 4 + 4)
    this.ReadFromArchive = new fbe.FieldModelBool(buffer, this.RecordIds.fbeOffset + this.RecordIds.fbeSize)
    this.UseSnapshot = new fbe.FieldModelBool(buffer, this.ReadFromArchive.fbeOffset + this.ReadFromArchive.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.RecordIds.fbeSize + this.ReadFromArchive.fbeSize + this.UseSnapshot.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.RecordIds.fbeExtra + this.ReadFromArchive.fbeExtra + this.UseSnapshot.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradeHistoryByIdsParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4219
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.RecordIds.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RecordIds.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RecordIds.fbeSize

    if ((fbeCurrentSize + this.ReadFromArchive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ReadFromArchive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ReadFromArchive.fbeSize

    if ((fbeCurrentSize + this.UseSnapshot.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UseSnapshot.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSnapshot.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @param {!TradeHistoryByIdsParams} fbeValue Default value, defaults is new TradeHistoryByIdsParams()
   * @returns {!TradeHistoryByIdsParams} TradeHistoryByIdsParams value
   */
  get (fbeValue = new TradeHistoryByIdsParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @param {!TradeHistoryByIdsParams} fbeValue TradeHistoryByIdsParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.RecordIds.fbeSize) <= fbeStructSize) {
      this.RecordIds.get(fbeValue.RecordIds)
    } else {
      fbeValue.RecordIds.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RecordIds.fbeSize

    if ((fbeCurrentSize + this.ReadFromArchive.fbeSize) <= fbeStructSize) {
      fbeValue.ReadFromArchive = this.ReadFromArchive.get(false)
    } else {
      fbeValue.ReadFromArchive = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ReadFromArchive.fbeSize

    if ((fbeCurrentSize + this.UseSnapshot.fbeSize) <= fbeStructSize) {
      fbeValue.UseSnapshot = this.UseSnapshot.get(false)
    } else {
      fbeValue.UseSnapshot = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSnapshot.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @param {!TradeHistoryByIdsParams} fbeValue TradeHistoryByIdsParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradeHistoryByIdsParams}
   * @param {TradeHistoryByIdsParams} fbeValue TradeHistoryByIdsParams value
   */
  setFields (fbeValue) {
    this.RecordIds.set(fbeValue.RecordIds)
    this.ReadFromArchive.set(fbeValue.ReadFromArchive)
    this.UseSnapshot.set(fbeValue.UseSnapshot)
  }
}

export { FieldModelTradeHistoryByIdsParams };

/**
 * Fast Binary Encoding TradeHistoryByIdsParams model
 */
class TradeHistoryByIdsParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradeHistoryByIdsParams(this.buffer, 4)
  }

  /**
   * Get the TradeHistoryByIdsParams model
   * @this {!TradeHistoryByIdsParamsModel}
   * @returns {!FieldModelTradeHistoryByIdsParams} model TradeHistoryByIdsParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradeHistoryByIdsParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradeHistoryByIdsParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradeHistoryByIdsParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradeHistoryByIdsParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradeHistoryByIdsParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradeHistoryByIdsParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradeHistoryByIdsParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradeHistoryByIdsParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradeHistoryByIdsParamsModel}
   * @param {!TradeHistoryByIdsParams} value TradeHistoryByIdsParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradeHistoryByIdsParamsModel}
   * @param {!TradeHistoryByIdsParams} value TradeHistoryByIdsParams value, defaults is new TradeHistoryByIdsParams()
   * @return {!object} Deserialized TradeHistoryByIdsParams value and its size
   */
  deserialize (value = new TradeHistoryByIdsParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradeHistoryByIdsParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradeHistoryByIdsParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradeHistoryByIdsParamsModel}
   * @param {!number} prev Previous TradeHistoryByIdsParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradeHistoryByIdsParamsModel };
